<template>
  <!-- The demo FAB (plan §4.8, card D2): the collapsed pill carries the sim
       date, the sim time and the day, plus play/pause and the speed badge.
       Pressing the text (or the badge) opens the mini player above it.

       Exactly the agent FAB's geometry — same band (`z-40`), same safe-area
       offsets — because the two share one corner; when the hub keeps BOTH FABs
       (`replaceAgentFab` off) this one sits a row higher instead of on top.

       Hidden unless the hub says this is a demo AND wants a FAB: the mode block
       is the hub's word, never a local guess (`useDemoPlayerView.visible`). -->
  <div v-if="visible && model" class="fixed z-40 flex flex-col items-end" :style="anchorStyle">
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-2">
      <DemoPlayerCard v-if="expanded" @close="closePlayer" />
    </Transition>

    <div
      data-testid="demo-fab"
      class="flex items-center gap-0.5 rounded-full border border-gray-200 bg-white/95 shadow-lg backdrop-blur transition-shadow hover:shadow-xl dark:border-gray-700 dark:bg-gray-800/95">
      <button
        type="button"
        data-testid="demo-fab-pill"
        class="flex items-center gap-2 rounded-l-full py-2 pl-3 pr-2 text-xs font-medium text-gray-700 dark:text-gray-200"
        :title="tt('Demo clock — open the player', 'Demo ura — odpri predvajalnik')"
        @click="expanded = !expanded">
        <span class="h-1.5 w-1.5 flex-none rounded-full bg-amber-400"></span>
        <span class="whitespace-nowrap tabular-nums">{{ view.pillText.value }}</span>
      </button>

      <button
        type="button"
        data-testid="demo-fab-play"
        :disabled="!model.canPlay || view.demo.playbackPending.value"
        :title="model.playing ? tt('Pause the demo clock', 'Ustavi demo uro') : tt('Play the demo clock', 'Zaženi demo uro')"
        class="flex h-8 w-8 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
        @click="onPlayPause">
        <span class="material-symbols-outlined text-lg">{{ model.playing ? 'pause' : 'play_arrow' }}</span>
      </button>

      <button
        type="button"
        data-testid="demo-fab-speed"
        class="mr-0.5 rounded-full px-2 py-1.5 text-xs font-semibold tabular-nums text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        :title="tt('Speed — open the player to change it', 'Hitrost — odpri predvajalnik za spremembo')"
        @click="expanded = !expanded">
        {{ model.speedLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import DemoPlayerCard from './DemoPlayerCard.vue';
import { useDemoPlayerView } from '../../composables/useDemoPlayerView';
import { useDemoNarration } from '../../composables/useDemoNarration';
import { useDemoSceneStore } from '../../stores/demoScene';
import { tt } from '../../utils/consoleText';

const props = withDefaults(
  defineProps<{
    /** The agent FAB is visible too — sit a row above it, not on top of it. */
    stacked?: boolean;
  }>(),
  { stacked: false },
);

const view = useDemoPlayerView();
const narration = useDemoNarration();
const expanded = ref(false);

/**
 * The expanded scene's hand-off (card D8b): Settings → Demo → Playback's `⤢
 * scene` button navigates here and asks for the player to be OPEN, not just
 * present — an operator who pressed "open the scene" should not have to find the
 * pill again on the page it opened.
 */
const demoScene = useDemoSceneStore();
watch(
  () => demoScene.playerOpen,
  (open) => {
    if (open) expanded.value = true;
  },
  { immediate: true },
);

/** Closing the card is leaving the expanded scene. */
function closePlayer(): void {
  expanded.value = false;
  demoScene.closeScene();
}

const visible = computed(() => view.visible.value);
const model = computed(() => view.model.value);

/**
 * The agent FAB's own anchor (`1rem` from each safe edge) is the bottom row; a
 * stacked demo FAB clears a 3rem button plus a 0.75rem gap.
 */
const anchorStyle = computed(() => ({
  bottom: props.stacked ? 'calc(4.75rem + var(--safe-bottom))' : 'calc(1rem + var(--safe-bottom))',
  right: 'calc(1rem + var(--safe-right))',
}));

function onPlayPause(): void {
  const playing = model.value?.playing === true;
  // The FAB's play is the gesture the browser's audio policy wants: warm the
  // narration element here too (owner, 2026-10-22 — play from the FAB used to
  // start the show silently while the settings page's Play did not).
  narration.unlock();
  void view.demo.playback(playing ? 'pause' : 'play');
}
</script>
